import { useCallback, useEffect, useMemo, useState } from 'react'
import AdaptiveListDetail from '../components/AdaptiveListDetail.jsx'
import Attachments from '../components/Attachments.jsx'
import Sheet from '../components/Sheet.jsx'
import { addPlan, deletePlan, getPlans, setChargeStatus } from '../lib/plans.js'
import { currentMonthKey, parseAmount, peso, shortDate, todayISO } from '../lib/format.js'

/** Days between today and a due date. Negative means it has passed. */
function daysOut(due) {
  const a = new Date(todayISO() + 'T00:00:00')
  const b = new Date(due + 'T00:00:00')
  return Math.round((b - a) / 86400000)
}

function dueLabel(due) {
  const d = daysOut(due)
  if (d < 0) return { text: 'overdue ' + Math.abs(d) + 'd', cls: 'late' }
  if (d === 0) return { text: 'due today', cls: 'late' }
  if (d <= 7) return { text: 'in ' + d + 'd', cls: 'soon' }
  return { text: shortDate(due), cls: '' }
}

/**
 * Sorted by what is due next, because that is the question he opens this to
 * answer. Overdue is loud. Progress shows as pips and a count, so 7 of 12 is
 * readable at a glance and countable when he wants the number.
 */
export default function Plans({ toast, jump = null, onJumpHandled }) {
  const [plans, setPlans] = useState(null)
  const [charges, setCharges] = useState([])
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [selectedPlanId, setSelectedPlanId] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const { plans: p, charges: c } = await getPlans()
      setPlans(p)
      setCharges(c)
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load])

  const rows = useMemo(() => {
    if (!plans) return []
    return plans
      .map((p) => {
        const mine = charges.filter((c) => c.plan_id === p.id)
        const paid = mine.filter((c) => c.status === 'Paid')
        const next = mine.find((c) => c.status !== 'Paid')
        const remaining = mine
          .filter((c) => c.status !== 'Paid')
          .reduce((s, c) => s + Number(c.amount || 0), 0)
        return {
          ...p,
          charges: mine,
          paidCount: paid.length,
          next,
          remaining,
          closed: p.status === 'Closed',
          overdue: next ? daysOut(next.due_date) < 0 : false,
        }
      })
      .sort((a, b) => {
        if (a.closed !== b.closed) return a.closed ? 1 : -1
        if (!a.next) return 1
        if (!b.next) return -1
        return a.next.due_date.localeCompare(b.next.due_date)
      })
  }, [plans, charges])

  /* Arriving from a Log line: the installment log records the plan's id. */
  useEffect(() => {
    if (!jump || jump.type !== 'installment' || !plans) return
    if (plans.some((plan) => plan.id === jump.id)) setSelectedPlanId(jump.id)
    onJumpHandled?.()
  }, [jump, plans, onJumpHandled])

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your installments. {error}</p>
      </div>
    )
  }
  if (!plans) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  const selectedPlan = rows.find((plan) => plan.id === selectedPlanId) ?? null
  const newPlanAction = (
    <button type="button" className="btn ghost split-new-action"
      onClick={() => setSheet({ kind: 'new' })}>
      New installment
    </button>
  )

  return (
    <>
      <AdaptiveListDetail
        storageKey="mypersonalos.split.installmentsWidth"
        listLabel="Installments"
        selectedId={selectedPlan?.id ?? null}
        emptyLabel="Select an installment"
        toolbarAction={newPlanAction}
        list={(
          <>
            {rows.length === 0 ? (
              <div className="empty">
                <h2 className="serif">No installments yet</h2>
                <p>
                  Anything bought in instalments. Enter the total and the term and the app writes
                  the whole schedule, so you always know what is due next.
                </p>
              </div>
            ) : (
              <div className="card">
                <div className="rows">
                  {rows.map((plan) => {
                    const due = plan.next ? dueLabel(plan.next.due_date) : null
                    return (
                      <button
                        type="button"
                        className={'row plan-row' + (selectedPlan?.id === plan.id ? ' selected' : '')}
                        key={plan.id}
                        data-detail-id={plan.id}
                        aria-current={selectedPlan?.id === plan.id ? 'true' : undefined}
                        onClick={() => setSelectedPlanId(plan.id)}
                      >
                        <span className="nm">
                          <b>{plan.name}</b>
                          <span className="lbl">
                            {plan.paidCount} of {plan.term_count} paid · {peso(plan.remaining)} left
                          </span>
                        </span>
                        {plan.closed ? <span className="donetag">Paid off</span> : null}
                        {!plan.closed && due ? <em className={'due ' + due.cls}>{due.text}</em> : null}
                        <span className="go" aria-hidden="true">›</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}
        detail={selectedPlan ? (
          <PlanDetailSheet
            key={selectedPlan.id}
            plan={selectedPlan}
            allCharges={charges}
            onClose={() => setSelectedPlanId(null)}
            onChanged={load}
            onCharge={(charge) => setSheet({ kind: 'charge', plan: selectedPlan, charge })}
            onRemove={() => setSheet({ kind: 'remove', plan: selectedPlan })}
            toast={toast}
          />
        ) : null}
      />

      {sheet?.kind === 'new' ? (
        <PlanSheet
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'charge' ? (
        <Sheet
          title={sheet.plan.name + ' · ' + sheet.charge.seq + ' of ' + sheet.plan.term_count}
          onClose={() => setSheet(null)}
        >
          <p className="lbl" style={{ margin: '0 0 10px' }}>
            {peso(sheet.charge.amount)} due {shortDate(sheet.charge.due_date)}
            {sheet.charge.status === 'Paid' ? ' · paid' : ''}
          </p>
          <Attachments
            target={{ type: 'installment_charge', id: sheet.charge.id }}
            toast={toast}
          />
        </Sheet>
      ) : null}

      {sheet?.kind === 'remove' ? (
        <Sheet title={'Remove ' + sheet.plan.name + '?'} onClose={() => setSheet(null)}>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--ink-dim)' }}>
            This deletes the plan and all {sheet.plan.term_count} of its charges, including the
            ones already marked paid. It cannot be undone.
          </p>
          <div className="sheet-actions">
            <button type="button" className="btn ghost" onClick={() => setSheet(null)}>Keep it</button>
            <button type="button" className="btn" onClick={async () => {
              try {
                await deletePlan(sheet.plan.id)
                setSheet(null)
                setSelectedPlanId(null)
                load()
                toast('Removed')
              } catch (e) {
                toast(e.message || 'Could not remove that.')
              }
            }}>Remove</button>
          </div>
        </Sheet>
      ) : null}
    </>
  )
}

function PlanDetailSheet({ plan, allCharges, onClose, onChanged, onCharge, onRemove, toast }) {
  const due = plan.next ? dueLabel(plan.next.due_date) : null

  return (
    <Sheet title={plan.name} onClose={onClose}>
      <div className="plan-detail-summary">
        <div className="plan-top">
          <div>
            <span className="lbl">
              {[plan.merchant, plan.card_label].filter(Boolean).join(' · ') || 'No card noted'}
            </span>
          </div>
          {plan.closed ? <span className="donetag">Paid off</span> : null}
        </div>

        <div className="pips" aria-label={plan.paidCount + ' of ' + plan.term_count + ' paid'}>
          {plan.charges.map((charge) => (
            <i key={charge.id} className={charge.status === 'Paid' ? 'on' : ''} />
          ))}
        </div>

        <div className="plan-nums">
          <b className="num">{plan.paidCount} of {plan.term_count}</b>
          {plan.closed ? (
            <span className="lbl">{peso(plan.total_amount)} total</span>
          ) : (
            <>
              <span className="lbl">{peso(plan.remaining)} left</span>
              {due ? <em className={'due ' + due.cls}>{due.text}</em> : null}
            </>
          )}
        </div>

        {!plan.closed && plan.next ? (
          <p className="lbl" style={{ margin: '8px 0 0' }}>
            Next {peso(plan.next.amount)} on {shortDate(plan.next.due_date)}
          </p>
        ) : null}
      </div>

      <div className="card plan-schedule">
        <div className="rows">
          {plan.charges.map((charge) => {
            const paid = charge.status === 'Paid'
            const chargeDue = dueLabel(charge.due_date)
            return (
              <div className={'row' + (paid ? ' paid' : '')} key={charge.id} data-context-row="">
                <button
                  type="button"
                  className="tick"
                  aria-label={paid ? 'Mark pending' : 'Mark paid'}
                  aria-pressed={paid}
                  data-context-action=""
                  onClick={async () => {
                    try {
                      const result = await setChargeStatus(plan, charge, !paid, allCharges)
                      await onChanged()
                      if (result.closed) toast(plan.name + ' is paid off')
                    } catch (e) {
                      toast(e.message || 'Could not update that.')
                    }
                  }}
                >
                  ✓
                </button>
                <div className="nm">
                  <b style={{ fontWeight: 400 }}>{charge.seq} of {plan.term_count}</b>
                  <span className="lbl">{shortDate(charge.due_date)}</span>
                </div>
                {!paid && chargeDue.cls === 'late' ? <em className="due late">{chargeDue.text}</em> : null}
                <span className="amt num">{peso(charge.amount)}</span>
                <button
                  type="button"
                  className="clip"
                  aria-label={'Attachments for payment ' + charge.seq}
                  onClick={() => onCharge(charge)}
                >
                  ◫
                </button>
              </div>
            )
          })}
        </div>
      </div>

      <button type="button" className="btn ghost" style={{ marginTop: 14 }} onClick={onRemove}>
        Remove installment
      </button>
    </Sheet>
  )
}

/**
 * Total and term fill the monthly, or a typed monthly fills the total. Whichever
 * two he knows, the third follows — he should never have to do the division.
 */
function PlanSheet({ onClose, onSaved, toast }) {
  const [f, setF] = useState({
    name: '', merchant: '', cardLabel: '',
    total: '', term: '12', monthly: '', paidSoFar: '0',
    startMonth: currentMonthKey(), dueDay: '15', notes: '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))

  const total = parseAmount(f.total)
  const term = Math.max(1, parseInt(f.term, 10) || 1)
  const monthly = parseAmount(f.monthly)
  const paidSoFar = Math.min(term, Math.max(0, parseInt(f.paidSoFar, 10) || 0))

  function onTotal(v) {
    const t = parseAmount(v)
    setF((s) => ({
      ...s,
      total: v,
      monthly: t ? String(Math.round(t / Math.max(1, parseInt(s.term, 10) || 1))) : s.monthly,
    }))
  }
  function onTerm(v) {
    const n = Math.max(1, parseInt(v, 10) || 1)
    setF((s) => {
      const t = parseAmount(s.total)
      return { ...s, term: v, monthly: t ? String(Math.round(t / n)) : s.monthly }
    })
  }
  function onMonthly(v) {
    const m = parseAmount(v)
    setF((s) => ({
      ...s,
      monthly: v,
      total: m ? String(m * Math.max(1, parseInt(s.term, 10) || 1)) : s.total,
    }))
  }

  return (
    <Sheet title="New installment plan" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !f.name.trim() || !total) return
          setBusy(true)
          try {
            await addPlan({
              name: f.name,
              merchant: f.merchant,
              cardLabel: f.cardLabel,
              totalAmount: total,
              termCount: term,
              monthlyAmount: monthly || Math.round(total / term),
              startMonth: f.startMonth,
              dueDay: Math.min(31, Math.max(1, parseInt(f.dueDay, 10) || 1)),
              paidSoFar: paidSoFar,
              notes: f.notes,
            })
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="pl-name">What was bought</label>
          <input id="pl-name" required value={f.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="frow">
          <div className="field">
            <label className="lbl" htmlFor="pl-merch">Merchant</label>
            <input id="pl-merch" value={f.merchant} onChange={(e) => set('merchant', e.target.value)} />
          </div>
          <div className="field">
            <label className="lbl" htmlFor="pl-card">Card</label>
            <input id="pl-card" placeholder="Just a label"
              value={f.cardLabel} onChange={(e) => set('cardLabel', e.target.value)} />
          </div>
        </div>

        <div className="frow">
          <div className="field">
            <label className="lbl" htmlFor="pl-total">Total</label>
            <input id="pl-total" inputMode="numeric" required
              value={f.total} onChange={(e) => onTotal(e.target.value)} />
          </div>
          <div className="field">
            <label className="lbl" htmlFor="pl-term">Months</label>
            <input id="pl-term" inputMode="numeric"
              value={f.term} onChange={(e) => onTerm(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="pl-monthly">Monthly</label>
          <input id="pl-monthly" inputMode="numeric"
            value={f.monthly} onChange={(e) => onMonthly(e.target.value)} />
        </div>

        {/* Most of his plans were already running before the app existed.
            Typing 4 beats ticking four boxes, and it beats being shown four
            overdue rows on a plan that is perfectly up to date. */}
        <div className="field">
          <label className="lbl" htmlFor="pl-paid">Already paid</label>
          <input id="pl-paid" inputMode="numeric"
            value={f.paidSoFar} onChange={(e) => set('paidSoFar', e.target.value)} />
        </div>

        <div className="frow">
          <div className="field">
            <label className="lbl" htmlFor="pl-start">First month</label>
            <input id="pl-start" type="month" required
              value={f.startMonth} onChange={(e) => set('startMonth', e.target.value)} />
          </div>
          <div className="field">
            <label className="lbl" htmlFor="pl-day">Due day</label>
            <input id="pl-day" inputMode="numeric"
              value={f.dueDay} onChange={(e) => set('dueDay', e.target.value)} />
          </div>
        </div>

        {total && term ? (
          <p className="lbl" style={{ margin: '-4px 0 14px' }}>
            {term} charges, {peso(monthly || Math.round(total / term))} each, last one adjusted to
            land on {peso(total)}
            {paidSoFar > 0 && (
              <>
                {' · starts at '}{paidSoFar} of {term}
                {paidSoFar >= term ? ', already finished' : ''}
              </>
            )}
          </p>
        ) : null}

        <div className="field">
          <label className="lbl" htmlFor="pl-notes">Notes</label>
          <input id="pl-notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>

        <button className="btn" type="submit" data-sheet-primary disabled={busy || !f.name.trim() || !total}>
          {busy ? 'Saving…' : 'Create the schedule'}
        </button>
      </form>
    </Sheet>
  )
}
